import {type BskyAgent} from '@atproto/api'
import {type I18n} from '@lingui/core'
import {msg} from '@lingui/macro'

import {VIDEO_SERVICE_DID} from '#/lib/constants'
import {logger} from '#/logger'
import {UploadLimitError} from '#/lib/media/video/errors'
import {getServiceAuthAudFromUrl} from '#/lib/strings/url-helpers'
import {createVideoAgent} from './util'

export async function getServiceAuthToken({
  agent,
  aud,
  lxm,
  exp,
}: {
  agent: BskyAgent
  aud?: string
  lxm: string
  exp?: number
}) {
  const pdsAud = getServiceAuthAudFromUrl(agent.dispatchUrl)
  if (!pdsAud) {
    throw new Error('Agent does not have a PDS URL')
  }
  
  try {
    const {data: serviceAuth} = await agent.com.atproto.server.getServiceAuth({
      aud: aud ?? pdsAud,
      lxm,
      exp,
    })
    return serviceAuth.token
  } catch (e) {
    // Fallback: If getServiceAuth fails (e.g., custom PDS doesn't support it),
    // use the regular access token instead
    const error = e as any
    if (error?.status === 400 || error?.status === 404 || error?.status === 501) {
      console.warn('getServiceAuth not supported by PDS, falling back to access token', {
        status: error?.status,
        pdsUrl: agent.dispatchUrl,
      })
      if (agent.session?.accessJwt) {
        return agent.session.accessJwt
      }
      throw new Error('PDS does not support service auth and no access token available')
    }
    throw e
  }
}

export async function getVideoUploadLimits(agent: BskyAgent, _: I18n['_']) {
  try {
    const token = await getServiceAuthToken({
      agent,
      lxm: 'app.bsky.video.getUploadLimits',
      aud: VIDEO_SERVICE_DID,
    })
    const videoAgent = createVideoAgent()
    const {data: limits} = await videoAgent.app.bsky.video
      .getUploadLimits({}, {headers: {Authorization: `Bearer ${token}`}})
      .catch(err => {
        if (err instanceof Error) {
          throw new UploadLimitError(err.message)
        } else {
          throw err
        }
      })

    if (!limits.canUpload) {
      if (limits.message) {
        throw new UploadLimitError(limits.message)
      } else {
        throw new UploadLimitError(
          _(
            msg`You have temporarily reached the limit for video uploads. Please try again later.`,
          ),
        )
      }
    }
  } catch (e) {
    // For custom PDS, the video service might not be available
    // Log the error but allow upload to proceed (PDS will enforce its own limits)
    const error = e as any
    if (error?.status === 400 || error?.status === 404 || error?.status === 501 || error instanceof UploadLimitError) {
      // If it's a service error or actual limit error, handle it
      if (error instanceof UploadLimitError) {
        throw error
      }
      // For service errors (400/404/501), log warning but allow upload
      // The PDS itself will enforce upload limits
      console.warn('Video service upload limits check failed, proceeding with upload (PDS will enforce limits)', {
        status: error?.status,
        error: error?.message,
      })
      logger.warn('Video service upload limits check failed, proceeding with upload', {
        status: error?.status,
        error: error?.message,
      })
      return // Allow upload to proceed
    }
    // For other errors, rethrow
    throw e
  }
}
